/**
 * Page Liens de paiement Wave (/admin/rapprochement/wave/liens)
 * Module Checkout API — mode SANDBOX (production ready).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Link2, Plus, RefreshCw, Copy, ExternalLink, Ban, Search, Loader2,
  CheckCircle2, Clock, XCircle, FlaskConical, Wallet,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fxof = (v) => v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')} XOF`;
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v); }
};

const STATUS_META = {
  open: { label: 'En attente', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  complete: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  expired: { label: 'Expiré', color: 'bg-zinc-100 text-zinc-500 border-zinc-200', icon: XCircle },
};

const absoluteUrl = (u) => (u && u.startsWith('/') ? `${window.location.origin}${u}` : u);

const copyLink = (url) => {
  try {
    navigator.clipboard.writeText(absoluteUrl(url));
    toast({ title: '✓ Lien copié', description: absoluteUrl(url)?.slice(0, 70) });
  } catch {
    toast({ title: 'Erreur copie', variant: 'destructive' });
  }
};

// ═══════════════ Dialog création ═══════════════
const CreateLinkDialog = ({ open, onClose, onSuccess }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState('');
  const [payerMobile, setPayerMobile] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setSelected(null); setAmount(''); setPayerMobile(''); setCreated(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/wave/searchable-invoices`, { params: { q, limit: 15 } });
        setResults(r.data?.results || r.data?.items || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  const pick = (inv) => {
    setSelected(inv);
    setAmount(String(inv.amountDue || ''));
  };

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/wave/checkout/links`, {
        invoiceId: selected.id,
        sourceType: selected.sourceType,
        amount: amount ? parseInt(amount, 10) : null,
        payerMobile: payerMobile || null,
      });
      setCreated(r.data);
      onSuccess?.();
      toast({ title: '✓ Lien de paiement créé', description: `Facture ${selected.invoiceNumber}` });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Création impossible', variant: 'destructive' });
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-wave-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-sky-600" /> Nouveau lien de paiement Wave</DialogTitle>
          <DialogDescription>Recherchez la facture, ajustez le montant, générez le lien.</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-3" data-testid="created-link-result">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
              <div className="font-semibold text-emerald-800 mb-1">Lien généré ✓</div>
              <div className="text-emerald-700 break-all text-xs">{absoluteUrl(created.waveLaunchUrl)}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="copy-created-link-btn" className="flex-1" onClick={() => copyLink(created.waveLaunchUrl)}>
                <Copy className="w-4 h-4 mr-1.5" /> Copier le lien
              </Button>
              <Button variant="outline" onClick={() => window.open(absoluteUrl(created.waveLaunchUrl), '_blank')}>
                <ExternalLink className="w-4 h-4 mr-1.5" /> Ouvrir
              </Button>
            </div>
            <Button variant="ghost" className="w-full" onClick={onClose}>Fermer</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {!selected ? (
              <div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    data-testid="invoice-search-input"
                    className="pl-9"
                    placeholder="N° facture, opération ou client…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mt-2 max-h-64 overflow-y-auto divide-y border rounded-lg">
                  {searching && <div className="p-3 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Recherche…</div>}
                  {!searching && results.length === 0 && <div className="p-3 text-sm text-zinc-400">Aucune facture ouverte trouvée</div>}
                  {results.map((inv) => (
                    <button
                      key={`${inv.sourceType}-${inv.id}`}
                      data-testid={`invoice-result-${inv.invoiceNumber}`}
                      onClick={() => pick(inv)}
                      className="w-full text-left p-2.5 hover:bg-sky-50 transition-colors flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-zinc-800 truncate">{inv.invoiceNumber} <span className="font-normal text-zinc-500">· {inv.companyName}</span></div>
                        <div className="text-xs text-zinc-500">{inv.sourceType === 'contract' ? 'Contrat' : 'Opération spot'}{inv.route ? ` · ${inv.route}` : ''}</div>
                      </div>
                      <div className="text-sm font-bold text-zinc-700 whitespace-nowrap">{fxof(inv.amountDue)}</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{selected.invoiceNumber} · {selected.companyName}</div>
                    <div className="text-xs text-zinc-500">Restant dû : {fxof(selected.amountDue)}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Changer</Button>
                </div>
                <div>
                  <Label htmlFor="link-amount">Montant (XOF)</Label>
                  <Input id="link-amount" data-testid="link-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="link-mobile">N° Wave du payeur (optionnel — sécurise le lien)</Label>
                  <Input id="link-mobile" data-testid="link-payer-mobile-input" placeholder="+22507XXXXXXXX" value={payerMobile} onChange={(e) => setPayerMobile(e.target.value)} />
                </div>
              </>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button data-testid="submit-create-link-btn" disabled={!selected || saving || !amount} onClick={submit}>
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Générer le lien
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════ Page ═══════════════
export default function WavePaymentLinksPage() {
  const [data, setData] = useState({ items: [], stats: {}, sandbox: true });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wave/checkout/links`);
      setData(r.data);
    } catch (e) {
      toast({ title: 'Erreur chargement', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const expire = async (id) => {
    try {
      await axios.post(`${API}/wave/checkout/links/${id}/expire`);
      toast({ title: '✓ Lien expiré' });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const s = data.stats || {};

  return (
    <div className="p-6 space-y-5" data-testid="wave-payment-links-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center"><Link2 className="w-5 h-5 text-sky-600" /></span>
            Liens de paiement Wave
            {data.sandbox && (
              <Badge data-testid="sandbox-badge" className="bg-amber-100 text-amber-800 border-amber-300 gap-1">
                <FlaskConical className="w-3.5 h-3.5" /> MODE SANDBOX
              </Badge>
            )}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Générez des liens de paiement rattachés aux factures — lettrage automatique à réception du paiement.
            {data.sandbox && ' Aucun débit réel : les paiements sont simulés en attendant la clé API Wave.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-links-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="new-link-btn" className="bg-sky-600 hover:bg-sky-700">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau lien
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Liens en attente', value: s.open ?? 0, testid: 'kpi-open-links', icon: Clock, tint: 'text-blue-600 bg-blue-50' },
          { label: 'Liens payés', value: s.complete ?? 0, testid: 'kpi-complete-links', icon: CheckCircle2, tint: 'text-emerald-600 bg-emerald-50' },
          { label: 'Encaissé via liens', value: fxof(s.totalCollected ?? 0), testid: 'kpi-total-collected', icon: Wallet, tint: 'text-sky-600 bg-sky-50' },
          { label: 'Expirés', value: s.expired ?? 0, testid: 'kpi-expired-links', icon: XCircle, tint: 'text-zinc-500 bg-zinc-50' },
        ].map((k) => (
          <div key={k.label} data-testid={k.testid} className="bg-white border rounded-xl p-4 flex items-center gap-3">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.tint}`}><k.icon className="w-4.5 h-4.5" /></span>
            <div>
              <div className="text-lg font-bold text-zinc-900">{k.value}</div>
              <div className="text-xs text-zinc-500">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2.5">Facture</th>
              <th className="text-left px-4 py-2.5">Client</th>
              <th className="text-right px-4 py-2.5">Montant</th>
              <th className="text-left px-4 py-2.5">Statut</th>
              <th className="text-left px-4 py-2.5">Créé le</th>
              <th className="text-left px-4 py-2.5">Payé le</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="empty-links-state">Aucun lien de paiement. Créez le premier !</td></tr>
            )}
            {!loading && data.items.map((l) => {
              const meta = STATUS_META[l.status] || STATUS_META.open;
              return (
                <tr key={l.id} data-testid={`link-row-${l.invoiceNumber}`} className="hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 font-semibold text-zinc-800">{l.invoiceNumber}</td>
                  <td className="px-4 py-2.5 text-zinc-600">{l.companyName}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{fxof(l.amount)}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`${meta.color} gap-1`} data-testid={`link-status-${l.id}`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </Badge>
                    {l.reconcileStatus === 'error' && (
                      <div className="text-[10px] text-red-600 mt-0.5" title={l.reconcileError}>⚠ lettrage manuel requis</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs">{fmtDate(l.createdAt)}</td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs">{fmtDate(l.whenCompleted)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" title="Copier le lien" data-testid={`copy-link-${l.id}`} onClick={() => copyLink(l.waveLaunchUrl)}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      {l.status === 'open' && (
                        <>
                          <Button size="sm" variant="ghost" title="Ouvrir la page de paiement" onClick={() => window.open(absoluteUrl(l.waveLaunchUrl), '_blank')}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Expirer" data-testid={`expire-link-${l.id}`} className="text-red-500 hover:text-red-600" onClick={() => expire(l.id)}>
                            <Ban className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateLinkDialog open={showCreate} onClose={() => setShowCreate(false)} onSuccess={load} />
    </div>
  );
}
